/**
 * @render react
 * @name ArticleCard
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import truncate from 'truncate';

import { withPolaris } from 'Components/protons/Polaris';

import { Date } from 'Components/atoms/Date';
import { Image } from 'Components/atoms/Image';
import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';
import { List } from 'Components/molecules/List';
import { Rating } from 'Components/molecules/Rating';
import { Social } from 'Components/molecules/Social';

import './_style.scss';

/**
 * Article Card component
 *
 * @param {Object} props - the props object
 * @param {string} props.image - object containing image src and alt
 * @param {string} props.imageSize - image size
 * @param {string} props.link  - url for the article. The whole article card should be clickable.
 * @param {string} props.author - name of the author
 * @param {object} props.authorImage - url and alt for author avatar
 * @param {string} props.authorUrl - url for the author
 * @param {string} props.kicker - kicker label
 * @param {string} props.kickerUrl - kicker URL
 * @param {array} props.categories - Array of categories containing label and url
 * @param {string} props.date - date posted
 * @param {string} props.datePosition - date position. Values can be bottom or meta
 * @param {string} props.ratingPosition - stars rating position. Values can be before-title or after-title
 * @param {string} props.time - time when the article was published or last updated.
 * @param {string} props.title - title of the article
 * @param {boolean} props.isHeading - whether the article title will be an <h2> or a <p>
 * @param {number} props.headingSize - size of the heading
 * @param {node} props.html - article card teaser text
 * @param {string} props.excerpt - excerpt paragraph text
 * @param {string} props.layout - class to be appended to the layout modifier
 * @param {number} props.rating - rating number to show the stars
 * @param {boolean} props.loadStyles - Whether to apply the class names for styling
 * @param {string} props.cardStyle - Style for the card. Values can be featured/default/minimal/side
 * @param {string} props.type - Type of the card. Values can be sponsored or default for styling the kicker appropriately
 * @param {string} props.ctaLabel - cta label to render article card CTA
 * @param {object} props.extraClassNames - extra classes to be applied to the component
 * @param {object} props.context - extra information coming from Polaris provider
 * @param {number} props.articleIndex - index of article in list
 * @param {boolean} props.showImageMetadata - Whether to show the image caption and credits
 * @param {boolean} props.showIndex - Whether to show the rating number
 * @param {bool} props.showRating - Whether to show the ratings
 * @param {bool} props.showTime - Whether to show the time with the date.
 * @param {bool} props.showTitle - Whether to show the title.
 * @param {object} props.resourceCategory - Object containing:
 * @param {string} resourceLabel - label of the link
 * @param {string} resourceUrl - URL to the resource (external or internal URL) Default position: top left corner
 * @param {array} props.socialPlatforms - [Single Media] Social Platforms for sharing to social media
 * @param {array} props.extraContent - Additional content to appear under the media block
 * @param {bool} props.image.isLCPCandidate - Whether the article card is a candidate for LCP.
 *
 *
 * Style notes:
 *  -no-media - will be set if there is no image set for the card
 *  -with-resource - will be set if the card has resource label and url passed
 */

/**
 * getExcerpt returns the Teaser Text html if available and fallbacks to the excerpt string.
 */
const getExcerpt = ({ html, excerpt }) => {
  const data = html || excerpt;

  if (React.isValidElement(data)) {
    return data;
  }
  if (typeof data === 'string') {
    return truncate(data, 150);
  }
  return '';
};

const getTitleStringValue = (title) => {
  if (React.isValidElement(title)) {
    return title?.props?.html || title;
  }

  return title;
};

const ArticleCard = ({
  articleIndex,
  author,
  authorImage,
  authorUrl,
  cardStyle,
  categories,
  context: {
    cssPrefix,
    ratingConfig,
    siteUrl,
  },
  ctaLabel,
  date,
  datePosition,
  excerpt,
  extraClassNames,
  extraContent,
  headingSize,
  html,
  image,
  imageSize,
  isHeading,
  kicker,
  kickerPosition,
  kickerUrl,
  layout,
  link,
  linkExtraProps,
  loadStyles,
  rating,
  ratingPosition,
  resourceCategory: {
    resourceLabel,
    resourceUrl,
  },
  showDate,
  showImageMetadata,
  showIndex,
  showRating,
  showTime,
  showTitle,
  socialPlatforms,
  time,
  title,
  type,
}) => {
  // don't display article meta wrapper div if no date
  const noMeta = !showIndex
    && !kicker
    && !author
    && (!categories || categories.length === 0)
    && !date
    && !time;

  const kickerBlock = (position) => kicker && (
    <span className={`${cssPrefix}__article-card--kicker${position ? ` -${position}` : ''}`}>
      <Link href={kickerUrl}>{kicker}</Link>
    </span>
  );

  const hasRating = rating && rating > 0 && showRating;
  const ratingBlock = (position) => (hasRating ? (
    <Rating
      rating={rating}
      numberOfStars={5}
      iconPath={ratingConfig.iconPath}
      iconViewBox={ratingConfig.iconViewBox}
      widgetDimensions="17px"
      widgetSpacings="2px"
      extraClassNames={{
        [`${cssPrefix}__article-card--rating`]: true,
        [`-${position}`]: position,
      }}
    />
  ) : null);

  return (
    <div
      className={classNames({
        [`${cssPrefix}__article-card`]: loadStyles,
        [`-layout-${layout}`]: layout,
        [`-${cardStyle}`]: true,
        [`-${type}`]: true,
        '-with-index': showIndex,
        '-with-rating': hasRating,
        '-with-resource': resourceLabel,
        '-no-media': !image || (image && image.src === ''),
        ...extraClassNames,
      })}
    >
      {link && (
        <Link
          extraClassNames={{ [`${cssPrefix}__article-card--link`]: true }}
          href={link}
          {...linkExtraProps}
        >
          {title && (
            <span className="screen-reader-text">{title}</span>
          )}
        </Link>
      )}

      {cardStyle && cardStyle === 'partnership' && (
        <div className={`${cssPrefix}__article-card--content`}>
          {title && (
            <Heading
              size={headingSize}
              extraClassNames={{
                [`${cssPrefix}__article-card--title`]: true,
              }}
            >
              <span>{title}</span>
            </Heading>
          )}
        </div>
      )}

      {image && image.src && (
        <div className={`${cssPrefix}__article-card--media`}>
          {/* Card style featured, use bigger image once we use Cloudinary */}
          <Image
            {...image}
            displayImageMetadata={showImageMetadata}
            lazyLoad={(image.lazyLoad !== undefined) ? image.lazyLoad : true}
            size={imageSize || (layout === 'featured' ? 'hero' : 'card')}
            isLCPCandidate={(image.isLCPCandidate !== undefined) ? image.isLCPCandidate : false}
          />
          {resourceLabel && (
            <span
              className={`${cssPrefix}__article-card--resource -${resourceLabel.toLowerCase().replace(/\s+/g, '')}`}
            >
              <Link
                href={resourceUrl}
                extraClassNames={{
                  [`${cssPrefix}__article-card--resource-label`]: true,
                }}
              >
                {resourceLabel}
              </Link>
            </span>
          )}
          {ratingPosition === 'over-image' && ratingBlock(ratingPosition)}
        </div>
      )}

      {type && type !== 'cover' && (cardStyle && cardStyle !== 'partnership') && (
        <div className={`${cssPrefix}__article-card--content`}>
          {!noMeta && (
            <div className={`${cssPrefix}__article-card--meta`}>
              {showIndex && (
                <span className={`${cssPrefix}__article-card--index`}>
                  {articleIndex}
                  <span>.</span>
                </span>
              )}

              {kickerPosition === 'top' && kickerBlock(kickerPosition)}

              {author && (
                <span className={`${cssPrefix}__article-card--author`}>
                  {authorImage && <Image {...authorImage} size="author-small" />}
                  {authorUrl ? (
                    <Link href={authorUrl}>{author}</Link>
                  ) : (
                    <span>{author}</span>
                  )}
                </span>
              )}

              {categories.length !== 0 && (
                <div className={`${cssPrefix}__article-card--categories`}>
                  <List content={categories} />
                </div>
              )}

              {(date || time) && (datePosition === 'meta' || datePosition === 'both') && (
                <>
                  <div className={`${cssPrefix}__article-card--date`}>
                    {(showDate && date) && <Date date={date} />}
                    {(showTime && time) && <span className={`${cssPrefix}__time`}>{time}</span>}
                  </div>
                  <noscript>
                    <div className={`${cssPrefix}__article-card--date no-script`}>
                      {date && <Date date={date} />}
                    </div>
                  </noscript>
                </>
              )}
            </div>
          )}

          {ratingPosition === 'before-title' && ratingBlock(ratingPosition)}

          {showTitle && title && isHeading
          && (
            <Heading
              size={headingSize}
              extraClassNames={{
                [`${cssPrefix}__article-card--title`]: true,
              }}
            >
              <span>{title}</span>
            </Heading>
          )}

          {(showTitle) && !isHeading && (
            <p className={`${cssPrefix}__article-card--title`}>
              {showTitle && title && (
                <span>{title}</span>
              )}
            </p>
          )}

          {ratingPosition === 'after-title' && ratingBlock(ratingPosition)}

          {excerpt && (
            <div className={`${cssPrefix}__article-card--excerpt`}>{getExcerpt({ html, excerpt })}</div>
          )}

          {(date || (showTime && time)) && (datePosition === 'bottom' || datePosition === 'both') && (
            <div className={`${cssPrefix}__article-card--meta--bottom`}>
              {kickerPosition === 'bottom' && kickerBlock(kickerPosition)}
              <div className={`${cssPrefix}__article-card--date -bottom`}>
                {(showDate && date) && <Date date={date} />}
                {(showTime && time) && <span className={`${cssPrefix}__time`}>{time}</span>}
              </div>
              <noscript>
                <div className={`${cssPrefix}__article-card--date no-script`}>
                  {date && <Date date={date} />}
                </div>
              </noscript>
            </div>
          )}

          {ctaLabel && (
            <Link href={link} extraClassNames={{ [`${cssPrefix}__article-card--cta`]: true }}>
              {ctaLabel}
            </Link>
          )}

          {socialPlatforms && Array.isArray(socialPlatforms) && link && (
            <Social
              title={getTitleStringValue(title)}
              url={`${siteUrl}${link}`}
              platforms={socialPlatforms}
            />
          )}
        </div>
      )}

      {cardStyle && cardStyle === 'partnership' && extraContent && (
        <div className={`${cssPrefix}__article-card--extraContent`}>
          <div className="-extraContent--left">
            {extraContent.left}
          </div>
          <div className="-extraContent--right">
            {extraContent.right}
          </div>
        </div>
      )}
    </div>
  );
};

ArticleCard.propTypes = {
  articleIndex: PropTypes.number,
  author: PropTypes.string,
  authorImage: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string,
  }),
  authorUrl: PropTypes.string,
  cardStyle: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
  })),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
    ratingConfig: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    siteUrl: PropTypes.string,
  }),
  ctaLabel: PropTypes.string,
  date: PropTypes.string,
  datePosition: PropTypes.oneOf(['meta', 'bottom', 'default', 'both']),
  excerpt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
  extraClassNames: PropTypes.shape(),
  extraContent: PropTypes.shape({
    left: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
    ]),
    right: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
    ]),
  }),
  headingSize: PropTypes.number,
  html: PropTypes.node,
  image: PropTypes.shape({
    alt: PropTypes.string,
    lazyLoad: PropTypes.bool,
    src: PropTypes.string,
    isLCPCandidate: PropTypes.bool,
  }),
  imageSize: PropTypes.string,
  isHeading: PropTypes.bool,
  kicker: PropTypes.string,
  kickerPosition: PropTypes.oneOf(['top', 'bottom']),
  kickerUrl: PropTypes.string,
  layout: PropTypes.string,
  link: PropTypes.string,
  linkExtraProps: PropTypes.shape({
    onClick: PropTypes.func,
  }),
  loadStyles: PropTypes.bool,
  rating: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  ratingPosition: PropTypes.oneOf(['before-title', 'after-title', 'over-image']),
  resourceCategory: PropTypes.shape({
    resourceLabel: PropTypes.string,
    resourceUrl: PropTypes.string,
  }),
  showDate: PropTypes.bool,
  showImageMetadata: PropTypes.bool,
  showIndex: PropTypes.bool,
  showRating: PropTypes.bool,
  showTime: PropTypes.bool,
  showTitle: PropTypes.bool,
  socialPlatforms: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    platform: PropTypes.string,
  })),
  time: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  type: PropTypes.oneOf(['default', 'single_media', 'sponsored', 'cartoons', 'cover']),
};

ArticleCard.defaultProps = {
  articleIndex: null,
  author: null,
  authorImage: null,
  authorUrl: null,
  cardStyle: 'default',
  categories: [],
  context: {},
  ctaLabel: '',
  date: '',
  datePosition: 'bottom',
  excerpt: '',
  extraClassNames: {},
  extraContent: {
    left: '',
    right: '',
  },
  headingSize: 2,
  html: null,
  image: {
    alt: '',
    src: '',
    lazyLoad: true,
    isLCPCandidate: false,
  },
  imageSize: null,
  isHeading: true,
  kicker: '',
  kickerPosition: 'top',
  kickerUrl: '',
  layout: 'left',
  link: '',
  linkExtraProps: {
    onClick: () => {},
  },
  loadStyles: true,
  rating: undefined,
  ratingPosition: 'after-title',
  resourceCategory: {
    resourceLabel: '',
    resourceUrl: '',
  },
  showDate: true,
  showImageMetadata: false,
  showIndex: false,
  showRating: false,
  showTime: false,
  showTitle: true,
  socialPlatforms: null,
  time: null,
  title: '',
  type: 'default',
};

export default withPolaris(ArticleCard);

/**
 * Exported object for testing purposes.
 *
 * This object contains functions that are exported specifically for unit testing.
 * It allows the testing framework to access and test these functions directly.
 *
 * @type {Object}
 * @property {Function} getExcerpt - Function to get the excerpt or teaser text.
 * @property {Function} getTitleStringValue - Function to get the title string value.
 */
export const exportedForTesting = {
  getExcerpt,
  getTitleStringValue,
};
